/**
 * ErrorBoundary — last-line-of-defense wrapper around large route subtrees.
 *
 * When an unhandled render error fires beneath this boundary, the boundary
 * catches it (instead of letting React unmount the entire app and leave a
 * blank white page) and renders a friendly fallback with a "Try again"
 * button that resets the boundary state.
 *
 * Use sparingly — one per route is plenty. We don't want to swallow real
 * bugs invisibly, so the original error is also forwarded to the console.
 */
import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep the console trail so we can debug from production browser tools.
     
    console.error('[ErrorBoundary]', this.props.label || 'unknown', error, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-[#1B2838] border border-[#B98A2C]/15 rounded-2xl p-6 sm:p-7 shadow-2xl">
          <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-4">
            <AlertTriangle size={22} className="text-amber-400" />
          </div>
          <h2 className="text-[18px] font-extrabold text-white mb-1.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Something went sideways
          </h2>
          <p className="text-[13px] text-[#A8B8CA] leading-relaxed mb-5">
            {this.props.label
              ? <>This part of the app ({this.props.label}) hit an unexpected error and stopped rendering. Your data is safe — refresh or try the action again.</>
              : <>This part of the app hit an unexpected error and stopped rendering. Your data is safe — refresh or try the action again.</>}
          </p>
          {this.state.error?.message && (
            <details className="mb-5">
              <summary className="text-[11px] text-[#758A9F] cursor-pointer hover:text-white">Technical details</summary>
              <pre className="mt-2 text-[10.5px] text-rose-300/80 bg-[#0D1520] border border-white/[0.04] rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                {String(this.state.error.message).slice(0, 400)}
              </pre>
            </details>
          )}
          <div className="flex gap-2">
            <button
              data-testid="error-boundary-retry"
              onClick={this.reset}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-gradient-to-b from-[#CBA33B] to-[#B98A2C] text-[#0D1520] font-bold text-[13px] hover:from-[#D4AE45] hover:to-[#C09230]"
            >
              <RotateCcw size={13} /> Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-[#A8B8CA] font-semibold text-[13px]"
            >
              Hard reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
